/**
 * Movie Studio is a product shell around the same project document the
 * design editor already saves. A movie is not a second file format.
 */

import type {
  FabricCanvasData,
  Project,
  ProjectKind,
  ProjectPage,
  VideoProject,
} from '@canvas/shared-types';
import { applySizePreset } from '@/data/videoExportPresets';
import type { MotionTemplate } from '@/data/motionTemplates';
import { createBlankPage, resolveProjectForEditor } from '@/lib/projectPages';
import { createVideoProject } from '@/lib/video/sceneTimeline';
import { createClipProject, migrateVideoProject } from '@/lib/video/timelineEdit';

/**
 * Movie Studio list membership.
 * An explicit kind wins. Older motion projects have no kind and are movies
 * when their timeline is turned on.
 */
export function isMovieProject(project: {
  kind?: ProjectKind | null;
  video?: { enabled?: boolean } | null;
}): boolean {
  if (project.kind === 'movie') return true;
  if (project.kind === 'design') return false;
  return Boolean(project.video?.enabled);
}

/** Title for a movie copied from a design. */
export function movieNameFrom(sourceName: string): string {
  const base = sourceName.trim() || 'Untitled';
  const name = base.startsWith('Movie · ') ? base : `Movie · ${base}`;
  return name.length > 255 ? name.slice(0, 255) : name;
}

export function createMovieDocument(opts: {
  userId: string;
  name: string;
  template: MotionTemplate;
  now?: Date;
  projectId?: string;
}): Project {
  const now = opts.now ?? new Date();
  const page = createBlankPage(opts.template.width, opts.template.height, 'Scene 1', 1);
  const created = createClipProject(
    { width: opts.template.width, height: opts.template.height },
    now.getTime()
  );
  const video = {
    ...created,
    canvas: {
      width: opts.template.width,
      height: opts.template.height,
      bg: created.canvas?.bg || '#111111',
    },
    exportSettings: applySizePreset(created.exportSettings, opts.template.id, {
      width: opts.template.width,
      height: opts.template.height,
    }),
  };
  return movieRecord({
    projectId: opts.projectId ?? crypto.randomUUID(),
    userId: opts.userId,
    name: opts.name.trim() || opts.template.label,
    description: opts.template.hint,
    width: opts.template.width,
    height: opts.template.height,
    tags: ['movie', opts.template.id],
    pages: [page],
    activePageId: page.pageId,
    canvasData: page.canvasData,
    video,
    now,
    lastModifiedBy: opts.userId,
  });
}

/**
 * Copy a design's pages into a new movie. The original project is left
 * where it is; scenes in the copy are edited only inside Movie Studio.
 */
export function copyDesignToMovie(source: Project, userId: string, now = new Date()): Project {
  const resolved = resolveProjectForEditor(source);
  const video = migrateVideoProject(
    createVideoProject(
      resolved.pages,
      { width: source.width, height: source.height },
      now.getTime()
    ),
    { width: source.width, height: source.height }
  );
  return movieRecord({
    projectId: crypto.randomUUID(),
    userId,
    name: movieNameFrom(source.name),
    description: source.description || '',
    width: source.width,
    height: source.height,
    tags: source.tags?.includes('movie') ? source.tags : [...(source.tags || []), 'movie'],
    pages: resolved.pages,
    activePageId: resolved.activePageId,
    canvasData: resolved.canvasData,
    video,
    thumbnail: source.thumbnail,
    now,
    lastModifiedBy: userId,
  });
}

function movieRecord(opts: {
  projectId: string;
  userId: string;
  name: string;
  description: string;
  width: number;
  height: number;
  tags: string[];
  pages: ProjectPage[];
  activePageId: string;
  canvasData: FabricCanvasData;
  video: VideoProject;
  thumbnail?: string;
  now: Date;
  lastModifiedBy: string;
}): Project {
  return {
    projectId: opts.projectId,
    userId: opts.userId,
    name: opts.name,
    description: opts.description,
    kind: 'movie',
    canvasData: opts.canvasData,
    pages: opts.pages,
    activePageId: opts.activePageId,
    video: opts.video,
    thumbnail: opts.thumbnail,
    width: opts.width,
    height: opts.height,
    tags: opts.tags,
    status: 'draft',
    isPublished: false,
    showAuthorOnPublished: true,
    collaborators: [],
    versionCount: 1,
    fileSize: 0,
    viewCount: 0,
    forkCount: 0,
    commentsEnabled: true,
    usesAI: false,
    createdAt: opts.now,
    lastModifiedAt: opts.now,
    lastModifiedBy: opts.lastModifiedBy,
    updatedAt: opts.now,
  };
}
